import { useState, useRef, useEffect, useCallback } from 'react';

const OPENAI_CLIENT_SECRET_ENDPOINT = '/v1/realtime/client_secrets';
const OPENAI_TRANSCRIPTION_MODEL = 'gpt-4o-transcribe';
const OPENAI_TRANSCRIPTION_BASE_URL = "https://api.openai.com/v1/realtime/calls";

interface TranscriptionConfig {
  session: {
    type: 'transcription';
    audio: {
      input: {
        format?: {
          rate?: number;
          type: string;
        };
        noise_reduction?: {
          type?: 'near_field' | 'far_field';
        };
        transcription?: {
          model: string;
          language: string;
          prompt?: string;
        };
        turn_detection?: {
          type: string;
          threshold?: number;
          prefix_padding_ms?: number;
          silence_duration_ms?: number;
        };
      }
    };
    include?: string[];
  };
  expires_after?: {
    anchor?: 'created_at';
    seconds?: number; // integer, seconds after created at, 10 - 7200s (default 600)
  };
}

interface Event {
  type: string;
  session?: TranscriptionConfig;
  transcript?: string;
  delta?: string;
  item_id?: string;
  content_index?: number;
  error?: any;
}

class RTCSession {
  useToken: boolean;
  stream: MediaStream | null;
  peer: RTCPeerConnection | null;
  channel: RTCDataChannel | null;
  muted: boolean;
  isDevMode: boolean;
  onconnectionstatechange?: (state: string) => void;
  onmessage?: (parsed: Event) => void;
  ontrack?: (e: RTCTrackEvent) => void;
  onopen?: () => void;
  onerror?: (e: Error) => void;

  constructor() {
    this.useToken = true;
    this.stream = null;
    this.peer = null;
    this.channel = null;
    this.muted = false;
    this.isDevMode = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';
  }

  private devLog(...args: any[]) {
    if (this.isDevMode) {
      console.log(...args);
    }
  }

  private devError(...args: any[]) {
    if (this.isDevMode) {
      console.error(...args);
    }
  }

  async startTranscription(
    audioStream: MediaStream,
    config: TranscriptionConfig
  ): Promise<void> {
    await this.startInternal(
      audioStream,
      config
    );
  }

  stop(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    if (this.peer) {
      this.peer.close();
      this.peer = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.muted = false;
  }

  async startInternal(
    audioStream: MediaStream,
    config: TranscriptionConfig
  ): Promise<void> {
    this.stream = audioStream;
    this.peer = new RTCPeerConnection();
    // this.peer.ontrack = (event) => this.ontrack?.(event);
    this.peer.addTrack(audioStream.getTracks()[0]);
    // this.peer.onconnectionstatechange = () => {
    //   if (this.peer && this.onconnectionstatechange) {
    //     this.onconnectionstatechange(this.peer.connectionState);
    //   }
    // };
    this.channel = this.peer.createDataChannel("oai-events");

    const offer = await this.peer.createOffer();
    await this.peer.setLocalDescription(offer);
    try {
      const answer = await this.signal(offer, config);
      await this.peer.setRemoteDescription(answer);

      this.channel.onopen = () => this.onopen?.();
      this.channel.onmessage = (event) => {
        if (this.onmessage) {
          try {
            const parsed = JSON.parse(event.data);
            this.onmessage(parsed);
          } catch (err) {
            this.devError('Error parsing message:', err);
          }
        }
      };

    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.onerror?.(errorObj);
    }
  }

  // Function to establish a WebRTC connection with OpenAI
  async signal(
    offer: RTCSessionDescriptionInit,
    config: TranscriptionConfig
  ): Promise<RTCSessionDescriptionInit> {
    // 1. Set up Ephemeral Key for transcription session
    const sessionResponse = await fetch(
      '/.netlify/functions/openai-realtime-proxy',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: OPENAI_CLIENT_SECRET_ENDPOINT,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: config,
        }),
      }
    );

    if (!sessionResponse.ok) {
      const errorText = await sessionResponse.text();
      throw new Error(
        `Failed to request session token: ${sessionResponse.statusText} - ${errorText}`
      );
    }

    const data = await sessionResponse.json();
    const secret = data.value;

    // 2. With the client safe ephemeral key, make a request to set up WebRTC SDP
    const baseUrl = OPENAI_TRANSCRIPTION_BASE_URL;
    const sdpResponse = await fetch(`${baseUrl}`, {
        method: "POST",
        body: offer.sdp,
        headers: {
            Authorization: `Bearer ${secret}`,
            "Content-Type": "application/sdp",
        },
    });

    if (!sdpResponse.ok) {
      const errorText = await sdpResponse.text();
      this.devError(
        'Realtime connection failed:',
        sdpResponse.status,
        errorText
      );
      throw new Error(
        `Failed to signal: ${sdpResponse.statusText} - ${errorText}`
      );
    }

    const sdp = await sdpResponse.text();
    return { type: "answer", sdp }
  }
}

interface STTState {
  micAvailable: boolean;
  micPermissionAllowed: boolean;
  listening: boolean;
  transcribing: boolean;
  connecting: boolean;
  errorOccurred: boolean;
  errorMsg: string;
  transcript: string;
  interimTranscript: string;
  start: () => Promise<void>;
  stop: () => void;
  clear: () => void;
  isAudioActive: boolean;
}

const useSTT = (lang: string = 'en', prompt: string = ''): STTState => {
  const [micAvailable, setMicAvailable] = useState<boolean>(true);
  const [micPermissionAllowed, setMicPermissionAllowed] =
    useState<boolean>(true);
  const [listening, setListening] = useState<boolean>(false);
  const [isAudioActive, setIsAudioActive] = useState<boolean>(false);
  const [transcribing, setTranscribing] = useState<boolean>(false);
  const [connecting, setConnecting] = useState<boolean>(false);
  const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [transcript, setTranscript] = useState<string>('');
  const [interimTranscript, setInterimTranscript] = useState<string>('');
  const sessionRef = useRef<RTCSession | null>(null);
  const processedRef = useRef<Set<string>>(new Set());

  // Add refs for the states that need to be accessed in callbacks
  const listeningRef = useRef<boolean>(false);
  const connectingRef = useRef<boolean>(false);

  const isDevMode = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

  const devLog = (...args: any[]) => {
    if (isDevMode) {
      console.log(...args);
    }
  };

  const devError = (...args: any[]) => {
    if (isDevMode) {
      console.error(...args);
    }
  };

  // Sync state with refs
  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);

  useEffect(() => {
    connectingRef.current = connecting;
  }, [connecting]);

  const checkMic = async (): Promise<boolean> => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setMicAvailable(false);
      setErrorMsg('WebRTC is not supported by your browser.');
      setErrorOccurred(true);
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicAvailable(true);
      setMicPermissionAllowed(true);
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error: any) {
      setMicAvailable(true);
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        setMicPermissionAllowed(false);
        setErrorMsg('Microphone permission denied.');
        setErrorOccurred(true);
      } else if (
        error.name === 'NotFoundError' ||
        error.name === 'DevicesNotFoundError'
      ) {
        setMicAvailable(false);
        setErrorMsg('No microphone found.');
        setErrorOccurred(true);
      } else {
        setErrorMsg(`Error checking microphone: ${error.message}`);
        setErrorOccurred(true);
      }
      return false;
    }
  };

  const handleMsg = useCallback(
    (event: Event): void => {
      // devLog('Received WebRTC message:', event.type, event);

      try {
        switch (event.type) {
          case 'session.created':
            // Session properly created, can turn off connecting status
            setConnecting(false);
            setListening(true);
            break;

          case 'transcription_session.created':
            // Transcription session created, can turn off connecting status
            setConnecting(false);
            setListening(true);
            break;

          case 'conversation.item.input_audio_transcription.delta':
            // devLog('Transcription delta:', event.delta);
            if (event.delta) {
              setInterimTranscript((prev) => prev + event.delta);
            }
            break;

          case 'conversation.item.input_audio_transcription.completed':
            devLog('Transcription completed:', event.transcript);
            if (event.transcript) {
              const id = `${Date.now()}_${event.transcript}`;
              if (!processedRef.current.has(id)) {
                processedRef.current.add(id);
                setTranscript((prev) => prev + event.transcript + ' ');
              }
            }
            setInterimTranscript('');
            break;

          case 'conversation.item.input_audio_transcription.failed':
            devError('Transcription failed:', event.error);
            setErrorMsg('Transcription failed');
            setErrorOccurred(true);
            setTranscribing(false);
            break;

          case 'input_audio_buffer.speech_started':
            setIsAudioActive(true);
            break;

          case 'input_audio_buffer.speech_stopped':
            setIsAudioActive(false);
            break;

          case 'input_audio_buffer.committed':
          case 'conversation.item.added':
          case 'conversation.item.done':
            // These events can safely be ignored
            break;

          default:
            devLog('Unhandled message type:', event.type);
            break;
        }
      } catch (err) {
        devError(err)
      }
    },
    [lang, prompt, devLog, devError]
  );

  const handleErr = useCallback(
    (error: Error): void => {
      devError(error);
      setErrorMsg(error.message);
      setErrorOccurred(true);
      setListening(false);
      setConnecting(false);
      setTranscribing(false);
      if (sessionRef.current) {
        sessionRef.current.stop();
        sessionRef.current = null;
      }
    },
    [devError]
  );

  const start = useCallback(async (): Promise<void> => {
    // Use refs for immediate access to current state
    if (listeningRef.current || connectingRef.current) {
      devLog('Already listening or connecting, skipping start');
      return;
    }

    const available = await checkMic();
    if (!available) {
      return;
    }

    setTranscript('');
    setInterimTranscript('');
    setConnecting(true);
    setErrorOccurred(false);
    setErrorMsg('');
    processedRef.current.clear();

    sessionRef.current = new RTCSession();
    sessionRef.current.onconnectionstatechange = (state) => {
      devLog('WebRTC connection state changed:', state);
      if (state === 'connected') {
        // devLog('WebRTC connected successfully');
        setConnecting(false);
        setListening(true);
      } else if (
        state === 'disconnected' ||
        state === 'failed' ||
        state === 'closed'
      ) {
        devLog('WebRTC connection failed or disconnected:', state);
        stop();
      }
    };
    sessionRef.current.onopen = () => {
      // Session opened, can update connecting and listening status
      setConnecting(false);
      setListening(true);
    };
    sessionRef.current.onmessage = handleMsg;
    sessionRef.current.onerror = handleErr;

    const config: TranscriptionConfig = {
      session: {
        type: 'transcription',
        audio: {
          input: {
            transcription: {
              model: OPENAI_TRANSCRIPTION_MODEL,
              language: lang,
              prompt: prompt,
            },
            turn_detection: {
              type: 'server_vad',
              threshold: 0.3,
              prefix_padding_ms: 300,
              silence_duration_ms: 35,
            }
          }
        }
      }
    };

    try {
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      await sessionRef.current.startTranscription(micStream, config);
    } catch (error: any) {
      handleErr(error);
    }
  }, [lang, prompt, handleMsg, handleErr, devLog]);

  const stop = useCallback((): void => {
    devLog('[userChat] Stopping transcription')
    if (sessionRef.current) {
      sessionRef.current.stop();
      sessionRef.current = null;
    }
    setListening(false);
    setConnecting(false);
    setTranscribing(false);
  }, []);

  const clear = useCallback((): void => {
    setTranscript('');
    setInterimTranscript('');
    processedRef.current.clear();
  }, []);

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        devLog('[userChat useSTT] useEffect cleanup stopping function')
        sessionRef.current.stop();
        sessionRef.current = null;
      }
    };
  }, []);

  return {
    micAvailable,
    micPermissionAllowed,
    listening,
    transcribing,
    connecting,
    errorOccurred,
    errorMsg,
    transcript,
    interimTranscript,
    start,
    stop,
    clear,
    isAudioActive,
  };
};

export default useSTT;
